// components/list/useFactoryArchetypeMention.tsx
import React, { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { listFactoryPersonas } from './factoryPersonas';
import { listFactoryObjects } from './factoryObjects';
import { URW_ORGANISATION_ID } from '#lib/urwProjectDashboardAccess';
import { factoryMentionOptions } from './factoryMentionOptions';
import { captureListOutputMention, listOutputMentionOptions } from '#lib/list/listOutputMentions';
import { captureFactoryFolderMention } from '#lib/list/factoryFolderMentions';
import { OpenAIFolderIcon as Folder } from '@/components/ui/OpenAIFolderIcon';
import type { ListPromptEditorHandle } from '#types/types';

type MentionOption = ReturnType<typeof factoryMentionOptions>[number];

export function useFactoryArchetypeMention(
  editorRef: RefObject<ListPromptEditorHandle | null>,
  enabled: boolean,
  resolveWorkspaceId: () => string,
  projectId = '',
): {
  refreshMention: (event?: React.SyntheticEvent<HTMLElement>) => void;
  handleMentionKeyDown: (event: React.KeyboardEvent<HTMLElement>) => boolean;
  mentionMenu: React.ReactNode;
  closeMention: (nextFocus?: EventTarget | null) => void;
} {
  const { kernel } = useKernel();
  const [mention, setMention] = useState<{ query: string; start: number } | null>(null);
  const [options, setOptions] = useState<MentionOption[]>([]);
  const [optionsScope, setOptionsScope] = useState('');
  const scope = `${kernel.current?.getState().userContext?.dbUserId ?? ''}:${projectId}`;
  const [loadError, setLoadError] = useState('');
  const [selection, setSelection] = useState({ key: '', id: '' });
  const [outputLimit, setOutputLimit] = useState({ key: '', count: 3 });
  const menuRef = useRef<HTMLDivElement>(null);
  const sequence = useRef(0);
  const suppressedMention = useRef<{ start: number; query: string } | null>(null);

  const refreshMention = useCallback((event?: React.SyntheticEvent<HTMLElement>) => {
    if (!enabled) {
      setMention(null);
      return;
    }
    const snapshot = editorRef.current?.read();
    const caret = snapshot?.start ?? 0;
    const before = (snapshot?.text ?? '').slice(0, caret);
    const match = /(^|[\s(])@((?:(?:output|object|folder):)?[a-z0-9-]{0,80})$/i.exec(before);
    if (!match) {
      suppressedMention.current = null;
      setMention(null);
      return;
    }
    const start = caret - (match[2] ?? '').length - 1;
    const query = match[2] ?? '';
    if (event?.type === 'click') suppressedMention.current = null;
    if (suppressedMention.current?.start === start && suppressedMention.current.query === query) {
      setMention(null);
      return;
    }
    suppressedMention.current = null;
    setMention(previous => previous?.start === start && previous.query === query ? previous : { query, start });
  }, [enabled, editorRef]);

  const closeMention = useCallback((nextFocus?: EventTarget | null) => {
    if (nextFocus instanceof Node && menuRef.current?.contains(nextFocus)) return;
    setMention(null);
  }, []);

  useEffect(() => {
    const request = ++sequence.current;
    if (!mention || !enabled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- a closed menu must not display stale cards
      setOptions([]);
      return;
    }
    const workspaceId = resolveWorkspaceId();
    const timer = window.setTimeout(async () => {
      const outputOptions = projectId && kernel.current ? listOutputMentionOptions(kernel.current, projectId, true) : [];
      setOptions(factoryMentionOptions([], [], '', outputOptions));
      setOptionsScope(scope);
      if (!workspaceId && !projectId) return;
      try {
        const factoryScope = workspaceId ? { workspaceId } : { organisationId: URW_ORGANISATION_ID };
        const [body, people, products] = await Promise.all([
          kernel.current!.requestFactoryChange({ ...factoryScope, userId: kernel.current!.getState().userContext.dbUserId!, resource: 'archetypes', method: 'GET' }),
          listFactoryPersonas(kernel.current!, factoryScope),
          listFactoryObjects(kernel.current!, factoryScope),
        ]);
        if (!Array.isArray(body?.archetypes)) throw new Error('Factory mentions could not be loaded.');
        if (sequence.current !== request) return;
        setOptions([...factoryMentionOptions(body.archetypes, people.personas, mention.query, [], products.objects, body.referenceFolders), ...listOutputMentionOptions(kernel.current!, projectId, true)]);
        setLoadError('');
      } catch {
        if (sequence.current !== request) return;
        setOptions(factoryMentionOptions([], [], '', listOutputMentionOptions(kernel.current!, projectId, true)));
        setLoadError('Could not load Factory mentions. Type @ again to retry.');
      }
    }, 150);
    return () => { window.clearTimeout(timer); sequence.current += 1; };
  }, [enabled, mention, projectId, scope]);

  const queryKey = mention ? `${scope}:${mention.start}:${mention.query}` : '';
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- a new query or open starts a fresh menu
    setSelection({ key: queryKey, id: '' });
    setOutputLimit({ key: queryKey, count: 3 });
  }, [queryKey]);
  const needle = mention?.query.trim().toLowerCase() ?? '';
  const allOptions = optionsScope === scope ? options.filter(option => !needle || option.handle.toLowerCase().includes(needle) || option.name.toLowerCase().includes(needle)
    || (option.category === 'Folders' && option.handle.toLowerCase().replace(/[^a-z0-9]/g, '').includes(needle.replace(/[^a-z0-9]/g, '')))
    || (option.category === 'Outputs' && option.name.toLowerCase().replace(' ', ':').includes(needle))) : [];
  const outputOptions = allOptions.filter(option => option.category === 'Outputs');
  const visibleOutputCount = outputLimit.key === queryKey ? outputLimit.count : 3;
  const hasMoreOutputs = outputOptions.length > visibleOutputCount;
  const lastShortcut: MentionOption[] = optionsScope === scope && options.some(option => option.category === 'Outputs') && (!needle || 'last'.startsWith(needle))
    ? [{ id: 'last-output-shortcut', handle: 'last', name: '@last', category: 'Outputs' }] : [];
  const currentOptions = [...allOptions.filter(option => option.category !== 'Outputs'), ...outputOptions.slice(0, visibleOutputCount), ...lastShortcut];
  const selectable = currentOptions.filter(option => !option.unavailable);
  const selectableIndices = new Map(selectable.map((option, index) => [option, index]));
  const index = Math.max(0, selection.key === queryKey ? selectable.findIndex(option => `${option.category}:${option.id}` === selection.id) : 0);
  const showMoreOutputs = () => setOutputLimit({ key: queryKey, count: visibleOutputCount + 12 });
  const pick = useCallback((option: MentionOption) => {
    if (option.unavailable || optionsScope !== scope) return;
    const editor = editorRef.current;
    if (!editor || !mention) return;
    const { text, end } = editor.read();
    let handle = option.handle;
    if (option.category === 'Folders' && kernel.current) {
      const displayName = options.some(other => other.category === 'Folders' && other.id !== option.id && other.name === option.name) ? `${option.name} · ${option.id.slice(-6)}` : option.name;
      try { handle = captureFactoryFolderMention(kernel.current, projectId, { id: option.id, name: option.name, displayName }, text.slice(0, mention.start) + text.slice(end)); }
      catch (error) { kernel.current.handleError(error instanceof Error ? error.message : 'Could not select folder.'); return; }
    }
    if (option.category === 'Outputs' && option.handle !== 'last' && kernel.current) {
      try { handle = captureListOutputMention(kernel.current, projectId, option.id, text.slice(0, mention.start) + text.slice(end)); }
      catch (error) { kernel.current.handleError(error instanceof Error ? error.message : 'Could not select output.'); return; }
    }
    editor.replace(mention.start, end, `@${handle} `);
    suppressedMention.current = { start: mention.start, query: handle };
    setMention(null);
    requestAnimationFrame(() => {
      editor.focus();
      editor.select(mention.start + handle.length + 2, mention.start + handle.length + 2);
    });
  }, [kernel, mention, options, optionsScope, scope, projectId, editorRef]);

  const handleMentionKeyDown = useCallback((event: React.KeyboardEvent<HTMLElement>): boolean => {
    if (!mention) return false;
    if (event.key === 'Escape') { event.preventDefault(); suppressedMention.current = mention; setMention(null); return true; }
    if (hasMoreOutputs && event.key === 'PageDown') {
      event.preventDefault();
      setOutputLimit({ key: queryKey, count: visibleOutputCount + 12 });
      return true;
    }
    if (selectable.length === 0) return false;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const nextIndex = (index + (event.key === 'ArrowDown' ? 1 : -1) + selectable.length) % selectable.length;
      const next = selectable[nextIndex]!;
      setSelection({ key: queryKey, id: `${next.category}:${next.id}` });
      // Only deliberate keyboard navigation scrolls, and never the surrounding chat.
      const menu = menuRef.current;
      const row = menu?.querySelectorAll('[role="option"]:not(:disabled)')[nextIndex];
      if (menu && row) {
        const bounds = menu.getBoundingClientRect();
        const item = row.getBoundingClientRect();
        if (item.top < bounds.top) menu.scrollTop += item.top - bounds.top;
        else if (item.bottom > bounds.bottom) menu.scrollTop += item.bottom - bounds.bottom;
      }
      return true;
    }
    if (event.key === 'Tab') {
      event.preventDefault();
      const selected = selectable[index];
      if (selected) pick(selected);
      return true;
    }
    if (event.key === 'Enter') {
      suppressedMention.current = mention;
      setMention(null);
    }
    return false;
  }, [selectable, index, mention, pick, hasMoreOutputs, visibleOutputCount, queryKey]);

  const mentionMenu = mention && (currentOptions.length > 0 || loadError) ? (
    <div key={queryKey} ref={menuRef} role="listbox" aria-label="Factory mentions"
      onBlur={event => { if (!(event.relatedTarget instanceof Node && editorRef.current?.contains(event.relatedTarget))) closeMention(event.relatedTarget); }}
      className="mb-2 max-h-[min(40vh,320px)] overflow-y-auto [overflow-anchor:none] custom-scrollbar rounded-2xl border border-white/10 bg-[#2F2F2F] p-2 shadow-xl">
      {loadError && <div role="alert" className="px-3 py-2 text-sm text-white/60">{loadError}</div>}
      {(['Personas', 'Objects', 'Archetypes', 'Folders', 'Outputs'] as const).map(category => currentOptions.some(option => option.category === category) && (
        <div key={category} role="group" aria-label={category}>
          <div className="px-3 pb-1 pt-2 text-sm text-white/50">{category}</div>
          {currentOptions.filter(option => option.category === category).map(option => {
            const itemIndex = selectableIndices.get(option) ?? -1;
            return (
        <button
          key={option.id}
          type="button"
          role="option"
          aria-selected={itemIndex === index}
          disabled={Boolean(option.unavailable)}
          onMouseDown={(event) => { event.preventDefault(); }}
          onClick={() => pick(option)}
          onMouseMove={() => {
            if (itemIndex < 0) return;
            const id = `${option.category}:${option.id}`;
            setSelection(previous => previous.key === queryKey && previous.id === id ? previous : { key: queryKey, id });
          }}
          className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors disabled:opacity-45 ${itemIndex === index ? 'bg-white/10' : 'enabled:hover:bg-white/[0.06]'}`}
        >
          {category === 'Folders' ? <Folder aria-hidden="true" className="h-7 w-7 shrink-0 text-white/65" /> : option.thumbnailUrl ? <img src={option.thumbnailUrl} alt="" width={32} height={32} decoding="async" loading="lazy" className={`h-8 w-8 shrink-0 object-cover ${category === 'Personas' ? 'rounded-full' : 'rounded-lg'}`} /> : <span aria-hidden="true" className="h-8 w-8 shrink-0 rounded-lg bg-white/10" />}
          <span className="min-w-0"><span className="block truncate text-sm text-[#ECECEC]">{category === 'Outputs' || category === 'Folders' ? option.name : `@${option.handle}`}{category === 'Folders' && currentOptions.some(other => other.category === category && other.id !== option.id && other.name === option.name) ? ` · ${option.id.slice(-6)}` : ''}</span><span className="block truncate text-xs text-white/50">{option.handle === 'last' ? 'Edit latest output' : category === 'Folders' ? 'Explore images or use as references' : category === 'Outputs' ? 'Use as reference' : option.unavailable || option.name}</span></span>
        </button>
            );
          })}
        </div>
      ))}
      {hasMoreOutputs && <button type="button" aria-keyshortcuts="PageDown" className="w-full rounded-xl px-3 py-2 text-left text-xs text-white/60 hover:bg-white/[0.06] focus-visible:outline focus-visible:outline-white/50"
        onMouseDown={event => event.preventDefault()} onClick={showMoreOutputs}>Show more outputs ({outputOptions.length - visibleOutputCount})</button>}
    </div>
  ) : null;

  return { refreshMention, handleMentionKeyDown, mentionMenu, closeMention };
}
